import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { UserService } from '../user/user.service';
import { RegistrationFlowService } from '../registration-flow/registration-flow.service';
import { PaymentConfigService } from '../payment-config/payment-config.service';
import {
  ACCESS_TOKEN_KEY,
  B2C_ID_TOKEN_KEY,
  REFRESH_TOKEN_EXPIRES_AT_KEY,
} from 'src/app/constants/app.constants';
import { User } from 'src/app/interfaces/user';
import { ActivateEmailDTO } from 'src/app/interfaces/activateEmail';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from '../config/config.service';

export interface AuthParams {
  identifier: string;
  password?: string;
  authenticationType: UserAuthenticationType;
}

export enum UserAuthenticationType {
  Email = 0,
  PhoneNumber = 1,
  ChipNumber = 2,
  VetCode = 3,
}

export enum TokenType {
  WebRefreshTokenType = '2',
}

export interface RegisterParams {
  emailAddress: string;
  password: string;
  referrerCode?: string;
  newsAndUpdatesEmail?: boolean;
  newsAndUpdatesSms?: boolean;
  newsAndUpdatesTelephone?: boolean;
  thirdPartyCommunicationEmail?: boolean;
  thirdPartyCommunicationSms?: boolean;
}

export interface ClaimAccountParams {
  password: string;
  emailAddress: string;
  emailVerificationCode: string;
}

export interface ActivateNoEmailUserParams {
  password: string;
  noEmailAddress: string;
  newEmailAddress: string;
}

export interface TokenData {
  iat: number;
  exp: number;
  userId: number;
}

export interface PassUpdateWithToken {
  password: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthManagerService {
  private isBrowser: boolean;
  private refreshInFlight$: Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private userService: UserService,
    private registrationFlowService: RegistrationFlowService,
    private paymentConfigService: PaymentConfigService,
    private config: ConfigService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  get cookieDomain(): string {
    return this.config.get('cookieDomain');
  }

  // An expired JWT still counts while the refresh token is valid,
  // the next API call's 401 refreshes it
  get isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired() || this.hasRefreshSession;
  }

  // The refresh token is an HttpOnly cookie, so its expiry is kept separately in local storage
  get hasRefreshSession(): boolean {
    if (!this.isBrowser) return false;
    const refreshExpiresAt = localStorage.getItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
    return !!refreshExpiresAt && new Date(refreshExpiresAt).getTime() > Date.now();
  }

  get token(): string | null {
    if (this.isBrowser) {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    return null;
  }

  get tokenData(): TokenData | null {
    if (this.isBrowser) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      return this.jwtHelper.decodeToken(accessToken);
    }
    return null;
  }

  get isAdminImpersonating(): boolean {
    if (this.isBrowser) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const claims = this.jwtHelper.decodeToken(accessToken);
      return !!claims?.superUserId;
    }
    return false;
  }

  register(registerParams: RegisterParams) {
    // Register only sends an activation email, the user is logged in after activating it (activateEmail)
    return this.http
      .post<any>(`${environment.apiUrl}/api/identity/register`, registerParams)
      .pipe(map((registerResponse) => registerResponse.Result));
  }

  claimAccount(claimAccountParams: ClaimAccountParams) {
    return this.http.post<User>(
      `${environment.apiUrl}/api/identity/user/claim`,
      claimAccountParams,
    );
  }

  activateNoEmailUser(activateNoEmailUserParams: ActivateNoEmailUserParams) {
    return this.http.post<User>(
      `${environment.apiUrl}/api/identity/user/claim-noemail`,
      activateNoEmailUserParams,
    );
  }

  // Email activation logs the user in, the refresh token comes as an HttpOnly cookie like on login
  activateEmail(activateEmailData: ActivateEmailDTO): Observable<User> {
    return this.http
      .post<any>(
        `${environment.apiUrl}/api/identity/user/verify`,
        { ...activateEmailData, tokenType: TokenType.WebRefreshTokenType },
        { withCredentials: true },
      )
      .pipe(
        map((activatedUser) => {
          this.storeTokens(activatedUser.issuedToken);
          return activatedUser;
        }),
      );
  }

  login(authParams: AuthParams) {
    // Ask for the refresh token as an HttpOnly cookie,
    // withCredentials so the browser keeps that cookie from the response
    return this.http.post<any>(`${environment.apiUrl}/api/v2/identity/token`, { ...authParams, tokenType: TokenType.WebRefreshTokenType }, { withCredentials: true }).pipe(
      map(authData => {
        const token = authData.jwt.token;

        // verify user permissions
        const canAccessCustomerUI = this.verifyUserPermissions(token, "Can logon to Customer UI");

        // if can NOT access customer UI, then return false instead of user object
        if (!canAccessCustomerUI) return false;
        this.storeTokens(authData);

        return token;
      })
    )
  }

  // The refresh token lives in an HttpOnly cookie, so the request body is empty.
  // the browser attaches the cookie (withCredentials: true).
  // Concurrent callers share one request: the server rotates the cookie,
  // so another parallel call would send a stale one.
  refreshToken(): Observable<string> {
    this.refreshInFlight$ ??= this.http
      .post<any>(`${environment.apiUrl}/api/v2/identity/refresh`, {}, { withCredentials: true })
      .pipe(
        map((authData) => {
          this.storeTokens(authData);
          return authData.jwt.token;
        }),
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay(1),
      );
    return this.refreshInFlight$;
  }

  private storeTokens(authData: any): void {
    if (!this.isBrowser) return;
    const { idleExpiresAt, expiresAt } = authData.refreshToken;
    const refreshExpiresAt = new Date(idleExpiresAt) < new Date(expiresAt) ? idleExpiresAt : expiresAt;
    localStorage.setItem(ACCESS_TOKEN_KEY, authData.jwt.token);
    localStorage.setItem(REFRESH_TOKEN_EXPIRES_AT_KEY, refreshExpiresAt);
  }

  logout(): void {
    // Revoke the refresh token, the HttpOnly cookie is sent with withCredentials.
    // Skip the unauth interceptor, so a 401 here (expired JWT) doesn't refresh and restore the session
    this.http
      .post<void>(
        `${environment.apiUrl}/api/v2/identity/logout`,
        {},
        { withCredentials: true, headers: { 'Skip-Interceptors': 'unauth' } },
      )
      .pipe(
        finalize(() => {
          // navigate away for the B2C logout only after the call, so the redirect doesn't cancel it
          if (environment.isB2CLoginEnabled) {
            window.location.href = `${environment.msalConfig.authorities.signUpSignIn}/oauth2/v2.0/logout?post_logout_redirect_uri=${environment.brandMainPageUrl}`;
          }
        }),
      )
      .subscribe();
    this.removeToken();

    if (environment.isB2CLoginEnabled) {
      localStorage.clear();
      sessionStorage.clear();
    } else {
      this.router.navigate(['/']);
    }
  }

  removeToken(): void {
    if (this.isBrowser) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
      document.cookie = `${ACCESS_TOKEN_KEY}=;domain=${this.cookieDomain};path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }
    this.userService.user = null;
    this.registrationFlowService.clear();
    this.paymentConfigService.clear();
  }

  forgotPassword(authParams: AuthParams): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/api/identity/password/reset`, authParams);
  }

  setPasswordWithToken(passUpdateWithToken: PassUpdateWithToken): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}/api/identity/password/forgotten`,
      passUpdateWithToken,
    );
  }

  verifyUserPermissions(token, customerPermission: string): boolean {
    if (!token) {
      return false;
    }
    try {
    const decodedToken = this.jwtHelper.decodeToken(token);
    if (!decodedToken || !decodedToken.permission || typeof decodedToken.permission !== 'string') {
      return false; // Token is valid, but has no permission field
    }
    const permissions: string[] = decodedToken.permission.split(',');

    return permissions.includes(customerPermission);
    } catch (e) {
      // This catches errors if the token is malformed or expired
      console.error("Failed to decode JWT token:", e);
      return false;
    }
  }

  private getCookieValue = (name) => {
    if (!this.isBrowser) return;
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
  }

  public loginBasedOnDomainCookie(): boolean {
    if (!this.isBrowser) return;

    const cookieValue = this.getCookieValue(ACCESS_TOKEN_KEY);
    if (!cookieValue) return false;

    localStorage.setItem(ACCESS_TOKEN_KEY, cookieValue);
    return true;
  }

  // B2C: exchange the B2C id_token for JWT in the browser,
  // so the API can set its HttpOnly refresh cookie here
  // (a server-side exchange would lose it)
  public loginWithB2C(idToken: string): Observable<string> {
    return this.http
      .get<any>(`${environment.apiUrl}/api/identity/adb2c/login`, {
        params: { foreingWebToken: idToken },
        withCredentials: true,
      })
      .pipe(
        map((authData) => {
          this.storeTokens(authData);
          return authData.jwt.token;
        }),
      );
  }

  // When B2C form-posts to /b2c-login,
  // server.ts hands the id_token over in a short-lived cookie
  public loginBasedOnB2CCookie(): Observable<boolean> {
    const idToken = this.getCookieValue(B2C_ID_TOKEN_KEY);
    if (!idToken) return of(false);

    document.cookie = `${B2C_ID_TOKEN_KEY}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;`;

    return this.loginWithB2C(idToken).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
